import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { toast } from 'react-toastify';
import {
    FileText, Building2, Loader2,
    RotateCcw, Send, ChevronDown, CheckCircle,
    Navigation, Eye, EyeOff, IndianRupee,
} from 'lucide-react';

import CustomDatePicker from '../../components/CustomDatePicker';
import { formatIndianCurrency, getAmountDisplay } from '../../utilities/amountToTextHelper';

import {
    fetchAmendCostCenters,
    fetchPOByCC,
    fetchCCDetailsByCcCode,
    saveAmendPO,
    clearPONumbers,
    clearCCDetails,
    clearSaveResult,
    resetClientPOAmend,
    selectCostCentersArray,
    selectCostCentersLoading,
    selectPONumbersArray,
    selectPONumbersLoading,
    selectCCDetails,
    selectCCDetailsLoading,
    selectSaveStatus,
    selectSaveLoading,
    selectSaveError,
} from '../../slices/accountsSlice/clientPOAmendSlice';

// ── Constants ─────────────────────────────────────────────────────────────────

const MONTH_ABBR = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

const formatDateForAPI = (val) => {
    if (!val) return '';
    if (typeof val === 'string' && /^\d{2}-[A-Za-z]{3}-\d{4}$/.test(val)) return val;
    if (typeof val === 'string' && /^\d{4}-\d{2}-\d{2}/.test(val)) {
        const [yyyy, mm, dd] = val.split('T')[0].split('-');
        return `${dd}-${MONTH_ABBR[parseInt(mm, 10) - 1]}-${yyyy}`;
    }
    const d = new Date(val);
    if (isNaN(d.getTime())) return '';
    return `${String(d.getDate()).padStart(2, '0')}-${MONTH_ABBR[d.getMonth()]}-${d.getFullYear()}`;
};

// ── Shared UI ─────────────────────────────────────────────────────────────────

const inputCls =
    'w-full px-3.5 py-2.5 rounded-xl border-2 text-sm bg-white dark:bg-gray-800 text-gray-800 dark:text-gray-100 border-gray-200 dark:border-gray-700 focus:outline-none focus:ring-2 focus:border-indigo-500 focus:ring-indigo-100 dark:focus:ring-indigo-900/30 hover:border-gray-300 transition-all disabled:opacity-60 disabled:cursor-not-allowed';

const Label = ({ children, required }) => (
    <label className="block text-xs font-bold text-gray-600 dark:text-gray-400 uppercase tracking-wider mb-1.5">
        {children}{required && <span className="text-rose-500 ml-0.5">*</span>}
    </label>
);

const SelectIcon = ({ loading }) => (
    <div className="pointer-events-none absolute inset-y-0 right-3 flex items-center">
        {loading
            ? <Loader2 className="h-4 w-4 text-indigo-500 animate-spin" />
            : <ChevronDown className="h-4 w-4 text-gray-400" />}
    </div>
);

const SectionHeader = ({ icon: Icon, title, subtitle, action }) => (
    <div className="px-6 py-4 border-b border-gray-100 dark:border-gray-700 flex items-center justify-between bg-gray-50/60 dark:bg-gray-900/40 rounded-t-2xl">
        <div className="flex items-center gap-3">
            <Icon className="h-4 w-4 text-indigo-500" />
            <div>
                <h2 className="text-sm font-bold text-gray-800 dark:text-gray-100">{title}</h2>
                {subtitle && <p className="text-xs text-gray-400 dark:text-gray-500">{subtitle}</p>}
            </div>
        </div>
        {action}
    </div>
);

// ── Component ─────────────────────────────────────────────────────────────────

const AddNewClientPOAmend = ({ menuData }) => {
    const dispatch = useDispatch();
    const { userData } = useSelector((s) => s.auth);

    const roleId = userData?.roleId || userData?.RID || 0;
    const userName = userData?.userName || userData?.UserName || 'system';

    // ── Redux ──────────────────────────────────────────────────────────────
    const costCenters = useSelector(selectCostCentersArray);
    const ccLoading = useSelector(selectCostCentersLoading);
    const poNumbers = useSelector(selectPONumbersArray);
    const poNumbersLoading = useSelector(selectPONumbersLoading);
    const ccDetails = useSelector(selectCCDetails);
    const ccDetailsLoading = useSelector(selectCCDetailsLoading);
    const saveStatus = useSelector(selectSaveStatus);
    const saveLoading = useSelector(selectSaveLoading);
    const saveError = useSelector(selectSaveError);

    // ── Form state ────────────────────────────────────────────────────────
    const [costCenter, setCostCenter] = useState('');
    const [showCCDetails, setShowCCDetails] = useState(false);
    const [clientPOId, setClientPOId] = useState('');
    const [amendPono, setAmendPono] = useState('');
    const [amendCompletionDate, setAmendCompletionDate] = useState(new Date());
    const [amendPoValue, setAmendPoValue] = useState('');
    const [amendGst, setAmendGst] = useState('');

    // ── Init ──────────────────────────────────────────────────────────────
    useEffect(() => {
        dispatch(fetchAmendCostCenters());
        return () => {
            dispatch(resetClientPOAmend());
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [dispatch]);

    // Fetch PO numbers on CC change
    useEffect(() => {
        setClientPOId('');
        setShowCCDetails(false);
        dispatch(clearPONumbers());
        dispatch(clearCCDetails());
        if (costCenter) {
            dispatch(fetchPOByCC(costCenter));
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [costCenter]);

    // Save result effect
    useEffect(() => {
        if (saveStatus === 'success') {
            toast.success('Amend PO Details Added Successfully.');
            dispatch(clearSaveResult());
            handleFullReset();
        } else if (saveStatus === 'failed' && saveError) {
            toast.error(saveError);
            dispatch(clearSaveResult());
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [saveStatus]);

    // ── Handlers ──────────────────────────────────────────────────────────
    const handleFullReset = () => {
        setCostCenter(''); setShowCCDetails(false);
        setClientPOId('');
        setAmendPono(''); setAmendCompletionDate(new Date());
        setAmendPoValue(''); setAmendGst('');
        dispatch(resetClientPOAmend());
        dispatch(fetchAmendCostCenters());
    };

    const toggleCCDetails = () => {
        if (!costCenter) { toast.warn('Select Cost Center.'); return; }
        if (showCCDetails) {
            setShowCCDetails(false);
            dispatch(clearCCDetails());
        } else {
            dispatch(fetchCCDetailsByCcCode(costCenter));
            setShowCCDetails(true);
        }
    };

    const handleNumericChange = (setter) => (e) => {
        const val = e.target.value;
        if (val === '' || /^\d*\.?\d{0,2}$/.test(val)) setter(val);
    };

    const total = (parseFloat(amendPoValue) || 0) + (parseFloat(amendGst) || 0);

    const selectedPO = poNumbers.find((p) => String(p.ClientPOId) === String(clientPOId));

    const handleSubmit = () => {
        if (!costCenter) { toast.warn('Select Cost Center.'); return; }
        if (!clientPOId) { toast.warn('Select PO No.'); return; }
        if (!amendPono.trim()) { toast.warn('Enter Amend PO No.'); return; }
        if (!amendCompletionDate) { toast.warn('Select Completion Date.'); return; }
        if (!amendPoValue || parseFloat(amendPoValue) <= 0) { toast.warn('Enter Amend PO Value.'); return; }

        dispatch(saveAmendPO({
            oldPONO: selectedPO?.PONumber || '',
            amendpocompletiondate: formatDateForAPI(amendCompletionDate),
            amendpono: amendPono,
            amendpovalue: parseFloat(amendPoValue) || 0,
            amendtotalvalue: total,
            amendegst: parseFloat(amendGst) || 0,
            roleId,
            createdBy: userName,
        }));
    };

    // ── Derived ───────────────────────────────────────────────────────────
    const isBusy = saveLoading || ccLoading || poNumbersLoading;
    const selectedCCName = costCenters.find((c) => c.CC_Code === costCenter)?.CC_Name || '—';

    const showSummary = costCenter && clientPOId && amendPono.trim() &&
        amendCompletionDate && amendPoValue && total > 0;

    const canSubmit = showSummary && !saveLoading;

    // ── Render ────────────────────────────────────────────────────────────
    return (
        <div className="min-h-screen bg-gradient-to-br from-slate-50 via-indigo-50/30 to-purple-50/30 dark:from-gray-950 dark:via-gray-900 dark:to-gray-950 p-4 md:p-6">

            {/* ── Page Header ──────────────────────────────────────────── */}
            <div className="max-w-7xl mx-auto mb-6">
                <div className="relative overflow-hidden rounded-2xl bg-gradient-to-r from-indigo-600 via-purple-600 to-violet-700 shadow-xl shadow-indigo-500/20 p-7 text-white">
                    <div className="absolute inset-0 opacity-10"
                        style={{ backgroundImage: 'radial-gradient(circle at 20% 50%, white 1px, transparent 1px), radial-gradient(circle at 80% 20%, white 1px, transparent 1px)', backgroundSize: '30px 30px' }} />
                    <div className="absolute top-0 right-0 w-72 h-72 bg-purple-500 rounded-full -translate-y-1/2 translate-x-1/4 opacity-20 blur-3xl" />
                    <div className="relative flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
                        <div className="flex items-center gap-4">
                            <div className="w-14 h-14 rounded-2xl bg-white/15 backdrop-blur flex items-center justify-center shadow-lg border border-white/20">
                                <FileText className="h-7 w-7 text-white" />
                            </div>
                            <div>
                                <div className="flex items-center gap-2 mb-1">
                                    <span className="text-xs font-bold text-indigo-200 uppercase tracking-wider bg-white/10 px-2 py-0.5 rounded-full">Accounts Module</span>
                                </div>
                                <h1 className="text-2xl md:text-3xl font-black tracking-tight">
                                    {menuData?.name || 'Amended Purchase Order Information'}
                                </h1>
                                <p className="text-indigo-200 text-sm mt-0.5">Amend an existing client purchase order</p>
                            </div>
                        </div>
                        <div className="hidden sm:flex items-center gap-3 text-indigo-200">
                            <div className="text-right">
                                <p className="text-xs uppercase tracking-wider">Module</p>
                                <p className="text-sm font-bold text-white">Accounts / Client PO Amend</p>
                            </div>
                            <Navigation className="h-5 w-5 opacity-60" />
                        </div>
                    </div>
                </div>
            </div>

            <div className="max-w-7xl mx-auto space-y-6">

                {/* ── Section 1: Cost Center & PO ───────────────────────── */}
                <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-sm border border-gray-200 dark:border-gray-700">
                    <SectionHeader
                        icon={Building2}
                        title="Cost Center & PO Selection"
                        subtitle="Select the cost center and the PO to amend"
                        action={
                            <button
                                onClick={handleFullReset}
                                disabled={isBusy}
                                className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold text-gray-500 dark:text-gray-400 bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors border border-gray-200 dark:border-gray-600 disabled:opacity-50"
                            >
                                <RotateCcw className="h-3.5 w-3.5" /> Reset
                            </button>
                        }
                    />
                    <div className="p-6 md:p-8">
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 items-end">
                            <div>
                                <Label required>Cost Center</Label>
                                <div className="relative">
                                    <select
                                        className={`${inputCls} appearance-none pr-10`}
                                        value={costCenter}
                                        onChange={(e) => setCostCenter(e.target.value)}
                                        disabled={ccLoading || isBusy}
                                    >
                                        <option value="">{ccLoading ? 'Loading…' : '-Please Select-'}</option>
                                        {costCenters.map((c) => (
                                            <option key={c.CC_Code} value={c.CC_Code}>{c.CC_Code} — {c.CC_Name}</option>
                                        ))}
                                    </select>
                                    <SelectIcon loading={ccLoading} />
                                </div>
                            </div>
                            <div>
                                <button
                                    onClick={toggleCCDetails}
                                    disabled={!costCenter || isBusy}
                                    className="flex items-center justify-center gap-2 w-full px-4 py-2.5 rounded-xl text-sm font-semibold bg-gradient-to-r from-indigo-500 to-purple-600 text-white shadow hover:from-indigo-600 hover:to-purple-700 disabled:opacity-50 disabled:cursor-not-allowed transition-all"
                                >
                                    {ccDetailsLoading
                                        ? <><Loader2 className="h-4 w-4 animate-spin" /> Loading…</>
                                        : showCCDetails
                                            ? <><EyeOff className="h-4 w-4" /> Close Details</>
                                            : <><Eye className="h-4 w-4" /> View Details</>}
                                </button>
                            </div>
                            <div>
                                <Label required>PO No</Label>
                                <div className="relative">
                                    <select
                                        className={`${inputCls} appearance-none pr-10`}
                                        value={clientPOId}
                                        onChange={(e) => setClientPOId(e.target.value)}
                                        disabled={!costCenter || poNumbersLoading || isBusy}
                                    >
                                        <option value="">{!costCenter ? 'Select cost center first' : poNumbersLoading ? 'Loading…' : '-Please Select-'}</option>
                                        {poNumbers.map((p) => (
                                            <option key={p.ClientPOId} value={p.ClientPOId}>{p.PONumber}</option>
                                        ))}
                                    </select>
                                    <SelectIcon loading={poNumbersLoading} />
                                </div>
                            </div>
                        </div>

                        {showCCDetails && ccDetails && (
                            <div className="mt-6 rounded-xl border border-gray-200 dark:border-gray-700 bg-gray-50/60 dark:bg-gray-900/30 p-5">
                                <p className="text-[10px] font-bold text-gray-400 uppercase tracking-wider mb-3">Cost Center Details</p>
                                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                                    {[
                                        { label: 'CC Code', value: ccDetails.CC_Code },
                                        { label: 'CC Name', value: ccDetails.CC_Name },
                                        { label: 'CC Type', value: ccDetails.CC_Type },
                                        { label: 'CC Sub Type', value: ccDetails.CC_SubType },
                                        { label: 'Final Offer No', value: ccDetails.Final_Offerno },
                                        { label: 'Final Offer Date', value: ccDetails.Final_Offerdate ? formatDateForAPI(ccDetails.Final_Offerdate) : '—' },
                                        { label: 'Ref No', value: ccDetails.Ref_No },
                                        { label: 'Ref Date', value: ccDetails.Ref_Date ? formatDateForAPI(ccDetails.Ref_Date) : '—' },
                                        { label: 'Phone No', value: ccDetails.CC_Phoneno },
                                        { label: 'Incharge Name', value: ccDetails.CC_Inchargename },
                                        { label: 'Incharge Phone', value: ccDetails.CC_Incharge_Phno },
                                        { label: 'Address', value: ccDetails.Address, wide: true },
                                    ].map(({ label, value, wide }) => (
                                        <div key={label} className={wide ? 'md:col-span-4' : ''}>
                                            <p className="text-[10px] font-bold text-gray-400 uppercase tracking-wider mb-1">{label}</p>
                                            <p className="font-semibold text-gray-800 dark:text-gray-100 break-words">{value || '—'}</p>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}
                    </div>
                </div>

                {/* ── Section 2: Amend PO Information ──────────────────── */}
                <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-sm border border-gray-200 dark:border-gray-700">
                    <SectionHeader
                        icon={FileText}
                        title="Amend PO Information"
                        subtitle="New PO number and completion date"
                    />
                    <div className="p-6 md:p-8">
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                            <div>
                                <Label required>Amend PO No</Label>
                                <input
                                    type="text"
                                    className={inputCls}
                                    placeholder="Enter amended PO number"
                                    value={amendPono}
                                    onChange={(e) => setAmendPono(e.target.value)}
                                    disabled={isBusy}
                                />
                            </div>
                            <div>
                                <Label required>Amend PO Completion Date</Label>
                                <CustomDatePicker
                                    value={amendCompletionDate}
                                    onChange={setAmendCompletionDate}
                                    disabled={isBusy}
                                    minDate={new Date()}
                                />
                            </div>
                        </div>
                    </div>
                </div>

                {/* ── Section 3: Amend PO Value ─────────────────────────── */}
                <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-sm border border-gray-200 dark:border-gray-700">
                    <SectionHeader
                        icon={IndianRupee}
                        title="Amend PO Value"
                        subtitle="Basic value, GST and total"
                    />
                    <div className="p-6 md:p-8">
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                            <div>
                                <Label required>Amend PO Value (₹)</Label>
                                <input
                                    type="text"
                                    inputMode="decimal"
                                    placeholder="0.00"
                                    className={inputCls}
                                    value={amendPoValue}
                                    onChange={handleNumericChange(setAmendPoValue)}
                                    disabled={isBusy}
                                />
                            </div>
                            <div>
                                <Label>GST (₹)</Label>
                                <input
                                    type="text"
                                    inputMode="decimal"
                                    placeholder="0.00"
                                    className={inputCls}
                                    value={amendGst}
                                    onChange={handleNumericChange(setAmendGst)}
                                    disabled={isBusy}
                                />
                            </div>
                            <div>
                                <Label>Total (₹)</Label>
                                <input
                                    type="text"
                                    readOnly
                                    value={formatIndianCurrency(total)}
                                    className={`${inputCls} font-bold`}
                                />
                            </div>
                        </div>
                        {total > 0 && (
                            <p className="text-xs text-indigo-500 dark:text-indigo-400 mt-2 italic">
                                {getAmountDisplay(total).words}
                            </p>
                        )}
                    </div>
                </div>

                {/* ── Summary ───────────────────────────────────────────── */}
                {showSummary && (
                    <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-sm border border-gray-200 dark:border-gray-700">
                        <SectionHeader icon={CheckCircle} title="Amend PO Summary" subtitle="Review before submitting" />
                        <div className="p-6 md:p-8">
                            <div className="grid grid-cols-2 md:grid-cols-3 gap-4 text-sm">
                                {[
                                    { label: 'Cost Center', value: `${costCenter} — ${selectedCCName}` },
                                    { label: 'Old PO No', value: selectedPO?.PONumber },
                                    { label: 'Amend PO No', value: amendPono },
                                    { label: 'Completion Date', value: formatDateForAPI(amendCompletionDate) },
                                    { label: 'PO Value', value: `₹ ${formatIndianCurrency(parseFloat(amendPoValue) || 0)}` },
                                    { label: 'GST', value: `₹ ${formatIndianCurrency(parseFloat(amendGst) || 0)}` },
                                    { label: 'Total', value: `₹ ${formatIndianCurrency(total)}` },
                                    { label: 'Created By', value: userName },
                                    { label: 'In Words', value: getAmountDisplay(total).words, wide: true },
                                ].map(({ label, value, wide }) => (
                                    <div key={label} className={`bg-gray-50 dark:bg-gray-700/50 rounded-xl p-3 ${wide ? 'md:col-span-3' : ''}`}>
                                        <p className="text-[10px] font-bold text-gray-400 uppercase tracking-wider mb-1">{label}</p>
                                        <p className="font-semibold text-gray-800 dark:text-gray-100 break-words">{value ?? '—'}</p>
                                    </div>
                                ))}
                            </div>
                        </div>
                    </div>
                )}

                {/* ── Action Buttons ────────────────────────────────────── */}
                <div className="flex items-center justify-end gap-3 pb-6">
                    <button
                        onClick={handleFullReset}
                        disabled={isBusy}
                        className="flex items-center gap-2 px-5 py-2.5 rounded-xl text-sm font-semibold text-gray-500 dark:text-gray-400 bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors border border-gray-200 dark:border-gray-600 disabled:opacity-50"
                    >
                        <RotateCcw className="h-4 w-4" /> Reset
                    </button>
                    <button
                        onClick={handleSubmit}
                        disabled={!canSubmit}
                        className="flex items-center gap-2 px-6 py-2.5 rounded-xl font-semibold text-sm bg-gradient-to-r from-indigo-500 to-purple-600 text-white shadow-md shadow-indigo-500/30 hover:from-indigo-600 hover:to-purple-700 disabled:opacity-50 disabled:cursor-not-allowed transition-all"
                    >
                        {saveLoading
                            ? <><Loader2 className="h-4 w-4 animate-spin" /> Saving…</>
                            : <><Send className="h-4 w-4" /> Submit Amend PO</>}
                    </button>
                </div>

            </div>
        </div>
    );
};

export default AddNewClientPOAmend;
